import React, { useState, useRef, useEffect } from 'react';
import { X, Check, Minus, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import useObjectDefinitions from '../../hooks/useObjectDefinitions';

function InlineEditableField({
  label,
  value,
  fieldName,
  type = 'text',
  options = [],
  onSave,
  placeholder = 'Empty',
  displayValue,
  fieldDef,
}) {
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const [tagInput, setTagInput] = useState('');
  const inputRef = useRef(null);
  const tagInputRef = useRef(null);
  const { getById } = useObjectDefinitions();

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const startEditing = () => {
    if (type === 'tags') return;
    setLocalValue(value || '');
    setEditing(true);
  };

  useEffect(() => {
    if (editing && inputRef.current) {
      requestAnimationFrame(() => {
        if (inputRef.current) inputRef.current.focus();
      });
    }
  }, [editing]);

  const handleSave = () => {
    setEditing(false);
    const trimmed = typeof localValue === 'string' ? localValue.trim() : localValue;
    const newVal = trimmed || null;
    if (newVal !== (value || null)) {
      onSave(fieldName, newVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    else if (e.key === 'Escape') { setEditing(false); setLocalValue(value || ''); }
  };

  const handleSelectChange = (e) => {
    const newVal = e.target.value || null;
    setEditing(false);
    if (newVal !== (value || null)) onSave(fieldName, newVal);
  };

  const handleAddTag = (tag) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    const currentTags = Array.isArray(value) ? value : [];
    if (currentTags.includes(trimmed)) return;
    onSave(fieldName, [...currentTags, trimmed]);
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove) => {
    const currentTags = Array.isArray(value) ? value : [];
    onSave(fieldName, currentTags.filter((t) => t !== tagToRemove));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddTag(tagInput); }
    else if (e.key === 'Escape') { setTagInput(''); tagInputRef.current?.blur(); }
  };

  const rowClasses = "flex items-center justify-between py-2.5 px-2 rounded-md min-h-[40px]";
  const labelClasses = "text-xs font-medium text-gray-400 w-28 flex-shrink-0 uppercase tracking-wider";
  const emptyClasses = "text-gray-300 italic text-sm";
  const valueClasses = "text-sm text-gray-900 dark:text-gray-100";
  const editInputClasses = "flex-1 text-sm bg-transparent border border-gray-200 dark:border-gray-600 rounded px-2 py-0.5 h-[26px] text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300";

  // --- Tags (multi_select) ---
  if (type === 'tags') {
    const tags = Array.isArray(value) ? value : [];
    return (
      <div className={`${rowClasses} items-start`}>
        <span className={`${labelClasses} pt-1`}>{label}</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1 items-center">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded border border-gray-200 text-[11px] text-gray-600 font-normal group/tag"
              >
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="text-gray-300 hover:text-gray-500 transition-colors"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
            <input
              ref={tagInputRef}
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={() => { if (tagInput.trim()) handleAddTag(tagInput); }}
              placeholder={tags.length === 0 ? placeholder : 'Add...'}
              className="text-sm bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-300 min-w-[60px] w-20"
            />
          </div>
        </div>
      </div>
    );
  }

  // --- Select ---
  if (type === 'select') {
    const shown = displayValue || options.find((o) => o.value === value)?.label || value;
    return (
      <div className={`${rowClasses} hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer`} onClick={() => setEditing(true)}>
        <span className={labelClasses}>{label}</span>
        {editing ? (
          <select
            ref={inputRef}
            value={value || ''}
            onChange={handleSelectChange}
            onBlur={() => setEditing(false)}
            className={editInputClasses}
          >
            <option value="">\u2014</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <span className={`flex-1 text-right truncate ${value ? valueClasses : emptyClasses}`}>
            {shown || placeholder}
          </span>
        )}
      </div>
    );
  }

  // --- Boolean ---
  if (type === 'boolean') {
    const isTrue = value === true || value === 'true';
    const isFalse = value === false || value === 'false';
    return (
      <div
        className={`${rowClasses} hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer`}
        onClick={() => onSave(fieldName, isTrue ? false : true)}
      >
        <span className={labelClasses}>{label}</span>
        <span className="flex-1 flex justify-end">
          {isTrue ? (
            <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded bg-gray-900 text-white">
              <Check className="w-3 h-3" strokeWidth={2.5} />
            </span>
          ) : isFalse || !value ? (
            <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded border border-gray-200 text-gray-300">
              <Minus className="w-2.5 h-2.5" strokeWidth={2} />
            </span>
          ) : null}
        </span>
      </div>
    );
  }

  // --- URL ---
  if (type === 'url') {
    if (editing) {
      return (
        <div className={rowClasses}>
          <span className={labelClasses}>{label}</span>
          <input
            ref={inputRef}
            type="url"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder="https://..."
            className={editInputClasses}
          />
        </div>
      );
    }
    let hostname;
    if (value) {
      try { hostname = new URL(value).hostname.replace(/^www\./, ''); } catch { hostname = value; }
    }
    return (
      <div className={`${rowClasses} hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer group/row`} onClick={startEditing}>
        <span className={labelClasses}>{label}</span>
        {value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-1 text-right truncate inline-flex items-center justify-end gap-1 text-sm text-gray-500 hover:text-gray-900 transition-colors group/url"
          >
            <span className="underline decoration-gray-300 underline-offset-2 group-hover/url:decoration-gray-500 truncate">{hostname}</span>
            <ArrowUpRight className="w-3 h-3 opacity-0 group-hover/url:opacity-100 transition-opacity flex-shrink-0" />
          </a>
        ) : (
          <span className={`flex-1 text-right truncate ${emptyClasses}`}>{placeholder}</span>
        )}
      </div>
    );
  }

  // --- Number ---
  if (type === 'number') {
    if (editing) {
      return (
        <div className={rowClasses}>
          <span className={labelClasses}>{label}</span>
          <input
            ref={inputRef}
            type="number"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className={editInputClasses}
          />
        </div>
      );
    }
    return (
      <div className={`${rowClasses} hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer`} onClick={startEditing}>
        <span className={labelClasses}>{label}</span>
        <span className={`flex-1 text-right truncate ${value !== null && value !== undefined && value !== '' ? 'tabular-nums font-medium text-sm text-gray-700' : emptyClasses}`}>
          {value !== null && value !== undefined && value !== '' ? Number(value).toLocaleString() : placeholder}
        </span>
      </div>
    );
  }

  // --- Date ---
  if (type === 'date') {
    if (editing) {
      return (
        <div className={rowClasses}>
          <span className={labelClasses}>{label}</span>
          <input
            ref={inputRef}
            type="date"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className={editInputClasses}
          />
        </div>
      );
    }
    let formatted;
    if (value) {
      const d = new Date(value);
      const now = new Date();
      formatted = d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
        ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
      });
    }
    return (
      <div className={`${rowClasses} hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer`} onClick={startEditing}>
        <span className={labelClasses}>{label}</span>
        <span className={`flex-1 text-right truncate ${value ? 'text-sm text-gray-500 tabular-nums' : emptyClasses}`}>
          {formatted || placeholder}
        </span>
      </div>
    );
  }

  // --- Object Relationship ---
  if (type === 'relationship') {
    const display = displayValue;
    const relObjDefId = fieldDef?.options?.related_object_definition_id;
    const relOd = relObjDefId ? getById(relObjDefId) : null;
    const relSlug = relOd?.slug_plural || relOd?.slug;
    const href = relSlug && value ? `/data/${relSlug}/${value}` : null;
    return (
      <div className={`${rowClasses} hover:bg-gray-50 dark:hover:bg-gray-700/50`}>
        <span className={labelClasses}>{label}</span>
        <span className="flex-1 text-right truncate">
          {display && href ? (
            <Link
              to={href}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors group/rel"
            >
              <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-gray-100 text-gray-500 text-[9px] font-medium flex items-center justify-center border border-gray-200/60">
                {display.charAt(0).toUpperCase()}
              </span>
              <span className="truncate">{display}</span>
              <ArrowUpRight className="w-3 h-3 opacity-0 group-hover/rel:opacity-100 transition-opacity flex-shrink-0" />
            </Link>
          ) : display ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
              <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-gray-100 text-gray-500 text-[9px] font-medium flex items-center justify-center border border-gray-200/60">
                {display.charAt(0).toUpperCase()}
              </span>
              <span className="truncate">{display}</span>
            </span>
          ) : value ? (
            <span className="text-sm text-gray-400 truncate">{value}</span>
          ) : (
            <span className={emptyClasses}>{placeholder}</span>
          )}
        </span>
      </div>
    );
  }

  // --- User ---
  if (type === 'user') {
    const display = displayValue;
    return (
      <div className={`${rowClasses} hover:bg-gray-50 dark:hover:bg-gray-700/50`}>
        <span className={labelClasses}>{label}</span>
        <span className="flex-1 text-right truncate">
          {display ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
              <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-gray-100 text-gray-500 text-[9px] font-medium flex items-center justify-center border border-gray-200/60">
                {display.charAt(0).toUpperCase()}
              </span>
              <span className="truncate">{display}</span>
            </span>
          ) : value ? (
            <span className="text-sm text-gray-400 truncate">{value}</span>
          ) : (
            <span className={emptyClasses}>{placeholder}</span>
          )}
        </span>
      </div>
    );
  }

  // --- Text (default) ---
  return (
    <div
      className={`${rowClasses} hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer`}
      onClick={!editing ? startEditing : undefined}
    >
      <span className={labelClasses}>{label}</span>
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={editInputClasses}
        />
      ) : (
        <span className={`flex-1 text-sm text-right truncate ${value ? 'text-gray-900 dark:text-gray-100' : emptyClasses}`}>
          {value || placeholder}
        </span>
      )}
    </div>
  );
}

export default InlineEditableField;
